import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import {
  Store,
  Truck,
  Calculator,
  CreditCard,
  Mail,
  ChevronRight,
  Plug,
  Megaphone,
  HardDrive,
  Clapperboard,
  MapPin,
} from 'lucide-react';
import { DojoIcon } from '@/components/ui/DojoIcon';
import { PickupMapsPage } from './PickupMapsPage';
import { StoresPage } from './StoresPage';
import { CouriersPage } from './CouriersPage';
import { FakturowniaPage } from './FakturowniaPage';
import { EmailSettingsPage } from './EmailSettingsPage';
import { PaymentsPage } from './PaymentsPage';
import { FacebookAdsPage } from './FacebookAdsPage';
import { DojoIntegrationPage } from './hunter/DojoIntegrationPage';
import { GoogleDrivePage } from './GoogleDrivePage';
import { AdProductionIntegrationsPage } from './AdProductionIntegrationsPage';

const integrationGroups = [
  { name: 'Sprzedaż i rozliczenia', ids: ['stores', 'payments', 'accounting'] },
  { name: 'Dostawa i odbiór', ids: ['couriers', 'pickup-maps'] },
  { name: 'Komunikacja', ids: ['email'] },
  { name: 'Marketing i treści', ids: ['facebook', 'ad-production', 'dojo'] },
  { name: 'Pliki', ids: ['google-drive'] },
];

const integrationCategories = [
  { id: 'pickup-maps', name: 'Mapy punktów odbioru', icon: MapPin, description: 'Wybór punktu w kasie, bez nadawania przesyłek', path: '/integrations/pickup-maps', available: true },
  { id: 'dojo', name: 'Dojo Mistrzów AI', icon: DojoIcon, description: 'Połącz lokalnych Mistrzów i zapisuj wyniki ich pracy w Ecomflow', path: '/integrations/dojo', available: true },
  {
    id: 'stores',
    name: 'Sklepy internetowe',
    icon: Store,
    description: 'Importuj zamówienia i synchronizuj statusy',
    path: '/integrations/stores',
    available: true,
  },
  {
    id: 'couriers',
    name: 'Firmy kurierskie',
    icon: Truck,
    description: 'Twórz przesyłki i drukuj listy przewozowe',
    path: '/integrations/couriers',
    available: true,
  },
  {
    id: 'accounting',
    name: 'Księgowość',
    icon: Calculator,
    description: 'Generuj i wysyłaj faktury automatycznie',
    path: '/integrations/accounting',
    available: true,
  },
  {
    id: 'payments',
    name: 'Bramki płatnicze',
    icon: CreditCard,
    description: 'Zarządzaj płatnościami i weryfikuj transakcje',
    path: '/integrations/payments',
    available: true,
  },
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    description: 'Wysyłaj powiadomienia e-mail do klientów',
    path: '/integrations/email',
    available: true,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Megaphone,
    description: 'Reklamy, komentarze i zarządzanie kontem',
    path: '/integrations/facebook',
    available: true,
  },
  {
    id: 'google-drive',
    name: 'Dysk Google',
    icon: HardDrive,
    description: 'Zapisuj pliki multimedialne na zewnętrznym Dysku Google.',
    path: '/integrations/google-drive',
    available: true,
  },
  {
    id: 'ad-production',
    name: 'Produkcja reklam AI',
    icon: Clapperboard,
    description: 'HeyGen, Higgsfield i ElevenLabs dla reklam wideo.',
    path: '/integrations/ad-production',
    available: true,
  },
];

function IntegrationsOverview() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-text-primary">Integracje</h1>
        <p className="text-text-secondary mt-2">
          Zarządzaj połączeniami z zewnętrznymi usługami
        </p>
      </div>

      {integrationGroups.map(group => (
      <section key={group.name} className="space-y-3">
      <h2 className="text-lg font-semibold text-text-primary">{group.name}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {group.ids.map(id => integrationCategories.find(category => category.id === id)!).map((category, index) => (
          <Link
            key={category.id}
            to={category.available ? category.path : '#'}
            className={`card group animate-slide-up ${
              !category.available ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent-primary/30'
            }`}
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={(e) => !category.available && e.preventDefault()}
          >
            <div className="flex h-full items-center gap-4">
              <div className={`shrink-0 p-3 rounded-xl ${
                category.available
                  ? 'bg-gradient-to-br from-accent-primary to-accent-secondary'
                  : 'bg-surface-hover'
              }`}>
                <category.icon className={`w-6 h-6 ${
                  category.available ? 'text-dark-900' : 'text-text-muted'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">{category.name}</h3>

                </div>
                <p className="text-sm text-text-secondary mt-1">{category.description}</p>
                {!category.available && (
                  <span className="inline-block mt-3 text-xs text-text-muted bg-surface-hover px-2 py-1 rounded">
                    Wkrótce dostępne
                  </span>
                )}
              </div>
              {category.available && <ChevronRight className="h-5 w-5 shrink-0 text-text-muted group-hover:text-accent-primary transition-colors" />}
            </div>
          </Link>
        ))}
      </div>

      </section>
      ))}

      {/* Info Card */}
      <div className="card bg-gradient-to-r from-accent-secondary/10 to-accent-primary/10 border-accent-secondary/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-accent-secondary/20">
            <Plug className="w-6 h-6 text-accent-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Potrzebujesz innej integracji?</h3>
            <p className="text-text-secondary mt-1">
              Pracujemy nad nowymi integracjami. Jeśli potrzebujesz połączenia z konkretną usługą,
              daj nam znać - dodamy ją w pierwszej kolejności.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const location = useLocation();
  const isOverview = location.pathname === '/integrations';

  return (
    <div>
      {/* Breadcrumb for sub-pages */}
      {!isOverview && (
        <div className="mb-6 animate-fade-in">
          <Link
            to="/integrations"
            className="inline-flex items-center text-sm text-text-secondary hover:text-accent-primary transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Powrót do integracji
          </Link>
        </div>
      )}

      <Routes>
        <Route index element={<IntegrationsOverview />} />
        <Route path="engine" element={<Navigate to="/integrations/stores" replace />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="pickup-maps" element={<PickupMapsPage />} />
        <Route path="couriers" element={<CouriersPage />} />
        <Route path="accounting" element={<FakturowniaPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="email" element={<EmailSettingsPage />} />
        <Route path="facebook" element={<FacebookAdsPage />} />
        <Route path="dojo" element={<DojoIntegrationPage />} />
        <Route path="google-drive" element={<GoogleDrivePage />} />
        <Route path="ad-production" element={<AdProductionIntegrationsPage />} />
      </Routes>
    </div>
  );
}
